// Public /drops page — renders the admin-curated ordered list only.
// No filters, no sort, no CTAs, no empty-grid placeholder beyond a terse
// "no drops yet" line.

import { useEffect, useMemo, useState } from "react";
import { useCanonical, useSeoMeta } from "../lib/seo";
import TopUtilityBar from "../sections/TopUtilityBar";
import MainHeader from "../sections/MainHeader";
import Footer from "../sections/Footer";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";

// Preferred chip order; any other productType lands at the end.
const TYPE_ORDER = ["Keyboard", "Mouse", "Mousepad", "Keycap", "Audio", "Switch", "PCB Module", "Accessory"];

function DropCard({ p }) {
	return (
		<a
			href={`/p/${p.handle}`}
			data-testid="drops-card"
			className="group block border border-white/10 bg-[#0A0A0A] hover:border-[#FF9F1C] transition-colors"
		>
			<div className="aspect-square bg-[#111] overflow-hidden">
				{p.image ? (
					<img
						src={p.image}
						alt={p.title}
						loading="lazy"
						className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform duration-300"
					/>
				) : (
					<div className="w-full h-full flex items-center justify-center font-mono text-[11px] text-white/30 uppercase">
						No image
					</div>
				)}
			</div>
			<div className="p-4">
				<p className="font-display text-[15px] md:text-[16px] text-white uppercase leading-tight">
					{p.title}
				</p>
				{p.price && (
					<p className="mt-2 font-mono text-[13px] text-white/80">
						₹{Number(p.price).toLocaleString("en-IN")}
					</p>
				)}
			</div>
		</a>
	);
}

export default function DropsPage() {
	const [products, setProducts] = useState([]);
	const [loading, setLoading] = useState(true);
	const [err, setErr] = useState("");
	const [typeFilter, setTypeFilter] = useState("all");

	// Types present in the current window, in the preferred order.
	const { types, counts } = useMemo(() => {
		const c = {};
		for (const p of products) {
			const t = p.product_type || "Other";
			c[t] = (c[t] || 0) + 1;
		}
		const present = Object.keys(c);
		present.sort((a, b) => {
			const ia = TYPE_ORDER.indexOf(a);
			const ib = TYPE_ORDER.indexOf(b);
			return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib) || a.localeCompare(b);
		});
		return { types: present, counts: c };
	}, [products]);

	const visible = useMemo(
		() => (typeFilter === "all" ? products : products.filter((p) => (p.product_type || "Other") === typeFilter)),
		[products, typeFilter]
	);

	useCanonical("/drops");
	useSeoMeta({
		title: "Fresh Drops — New Gaming Gear in India | Loadout",
		description: "The newest keyboards, mice, mousepads and keycaps added to Loadout in the last 15 days — fresh drops for Indian gamers, with UPI and 18-month warranty.",
		path: "/drops",
	});

	useEffect(() => {
		let alive = true;
		(async () => {
			try {
				const r = await fetch(`${BACKEND}/api/drops`);
				const j = await r.json();
				if (!alive) return;
				setProducts(j.products || []);
			} catch {
				if (alive) setErr("Could not load drops.");
			} finally {
				if (alive) setLoading(false);
			}
		})();
		return () => { alive = false; };
	}, []);

	return (
		<div className="min-h-screen bg-[#0A0A0A] text-white">
			<TopUtilityBar />
			<MainHeader cartCount={0} />
			<main data-testid="drops-page" className="mx-auto max-w-[1440px] px-6 py-12 md:py-16">
				<div className="border-b border-[#1F1F1F] pb-8 md:pb-10">
					<div className="font-mono text-[11px] uppercase tracking-[0.16em] text-[#FF9F1C]">
						FRESH DROPS
					</div>
					<h1 className="mt-3 font-display text-[36px] md:text-[56px] leading-[0.95]">
						DROPS.
					</h1>
					<p className="mt-4 font-sans text-[15px] md:text-[16px] text-[#8A8A8A] leading-relaxed max-w-[640px]">
						Everything added to the shop in the last 15 days. New arrivals land here automatically and age out on their own.
					</p>
					{types.length > 1 && (
						<div data-testid="drops-type-filter" className="mt-6 flex flex-wrap gap-2">
							<button
								type="button"
								data-testid="drops-type-chip"
								onClick={() => setTypeFilter("all")}
								className={
									"font-mono text-[11px] uppercase tracking-[0.14em] border px-3.5 py-2 transition-colors " +
									(typeFilter === "all"
										? "border-[#FF9F1C] text-[#FF9F1C]"
										: "border-white/20 text-white/70 hover:border-white/50")
								}
							>
								All ({products.length})
							</button>
							{types.map((t) => (
								<button
									key={t}
									type="button"
									data-testid="drops-type-chip"
									onClick={() => setTypeFilter(t)}
									className={
										"font-mono text-[11px] uppercase tracking-[0.14em] border px-3.5 py-2 transition-colors " +
										(typeFilter === t
											? "border-[#FF9F1C] text-[#FF9F1C]"
											: "border-white/20 text-white/70 hover:border-white/50")
									}
								>
									{t} ({counts[t]})
								</button>
							))}
						</div>
					)}
				</div>

				<section className="mt-10">
					{loading && (
						<p data-testid="drops-loading" className="font-mono text-[11px] uppercase tracking-[0.16em] text-white/40">
							Loading…
						</p>
					)}
					{err && (
						<p data-testid="drops-error" className="font-mono text-[12px] text-red-400">
							{err}
						</p>
					)}
					{!loading && !err && products.length === 0 && (
						<p data-testid="drops-empty" className="font-mono text-[12px] uppercase tracking-[0.14em] text-white/40">
							No drops curated yet.
						</p>
					)}
					{!loading && !err && products.length > 0 && (
						<div
							data-testid="drops-grid"
							className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-6"
						>
							{visible.map((p) => (
								<DropCard key={p.handle} p={p} />
							))}
						</div>
					)}
				</section>
			</main>
			<Footer />
		</div>
	);
}
